import { LocationTypeWebRepositoryMapper } from './entities/location-type-web-repository-mapper';
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BaseResponse,
  Dawa,
  GetSegmentSuggestionsRequest,
  SegmentSuggestions,
  Location,
  LocationType,
  PublicLocation,
  PrivateLocationsForPP,
} from '../../../../core/domain/models';
import {
  CreateEditPrivateLocationRequest,
  CreateEditPublicLocationRequest,
  GetStopsForPlanRequest,
  PrivateLocationRequest,
  PublicLocationRequest,
} from '../../../../core/domain/requests';
import { LocationsRepository } from '../../../../core/repositories';
import { catchError, map, mergeMap, Observable, of, reduce } from 'rxjs';
import { LocationsApiUrls } from './locations-api-urls';
import { LocationWebEntity } from './entities/location-web-entity';
import { PublicLocationMapper } from './entities/public-location.web-repository-mapper';
import { PublicLocationWebEntity } from './entities/public-location.web-entity';
import { LocationWebRepositoryMapper } from './entities/location.web-repository-mapper';
import { DawaWebRepositoryMapper } from './entities/dawa-web-repository-mapper';
import { DawaWebEntity } from './entities/dawa-web-entity';
import { LocationTypeWebEntity } from './entities/location-type-web-entity';
import { CountryWebEntity } from './entities/location-country-web-entity';
import { SegmentSuggestionMapper } from './entities/segment-suggestions-web-repository-mapper';
import { GetSegmentSuggestionsWebEntity } from './entities/segment-suggestions-web-entity';
import { PrivateLocationsForPPWebEntity } from './entities/private-locations-for-PP-web-entity';
import { PrivateLocationsForPPMapper } from './entities/private-locations-for-PP-web-entity-mapper';
import { GetPrivateLocationsResponse } from 'master-locations-library/master-locations-library/src/lib/core/domain/models/get-private-location.model';
import { GetPrivateLocationWebEntity } from './entities/get-private-location-web-entity';
import { GetPrivateLocationWebRepositoryMapper } from './entities/get-private-location-web-entity-mapper';
import { SortingOptions } from 'master-locations-library/master-locations-library/src/lib/core/domain/models/sorting-option.model';
import { CountryEntity } from 'master-locations-library/master-locations-library/src/lib/core/domain/models/country.model';
import { injectEnvironment } from 'master-locations-library/master-locations-library/src/lib/core/environments/environment.token';
import { Gls } from 'master-locations-library/master-locations-library/src/lib/core/domain/models/gls.model';
import { GlsWebEntity } from './entities/gls-web-entity';
import { GlsWebRepositoryMapper } from './entities/gls-web-repository-mapper';

@Injectable()
export class LocationsWebRepository extends LocationsRepository {
  private dawaMapper = new DawaWebRepositoryMapper();
  private glsWebRepositoryMapper = new GlsWebRepositoryMapper();
  private locationMapper = new LocationWebRepositoryMapper();
  private getPrivateLocationMapper =
    new GetPrivateLocationWebRepositoryMapper();
  private publicLocationMapper = new PublicLocationMapper();
  private locationTypeWebRepositoryMapper =
    new LocationTypeWebRepositoryMapper();
  private privateLocationsForPPMapper = new PrivateLocationsForPPMapper();
  private segmentSuggestionMapper = new SegmentSuggestionMapper();
  environment = injectEnvironment();

  constructor(private http: HttpClient) {
    super();
  }

  createEditPublicLocation(
    request: CreateEditPrivateLocationRequest
  ): Observable<CreateEditPrivateLocationRequest> {
    return this.http
      .post<BaseResponse<CreateEditPrivateLocationRequest>>(
        `${this.environment.url}${LocationsApiUrls.CreateEditPrivateLocation}`,
        request
      )
      .pipe(
        map((data: BaseResponse<CreateEditPrivateLocationRequest>) => {
          return data.result;
        }),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      );
  }

  getPrivateLocation(locationId: string): Observable<Location> {
    return this.http
      .get<BaseResponse<LocationWebEntity>>(
        `${this.environment.url}${LocationsApiUrls.GetPrivateLocation}`,
        {
          params: { privateLocationId: locationId },
        }
      )
      .pipe(
        map((response) => response.result),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.locationMapper.mapFrom));
  }

  deletePublicLocation(publicLocationId: string): Observable<boolean> {
    return this.http
      .delete<BaseResponse<unknown>>(
        `${this.environment.url}${LocationsApiUrls.DeletePrivateLocation}`,
        {
          params: { privateLocationId: publicLocationId },
        }
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      );
  }

  /**
   * used to deactivate public location
   * @param publicLocationId
   * @returns true/false
   */
  deactivatePublicLocation(publicLocationId: string): Observable<boolean> {
    return this.http
      .patch<BaseResponse<unknown>>(
        `${this.environment.url}${LocationsApiUrls.DeactivatePrivateLocation}`,
        null,
        {
          params: { privateLocationId: publicLocationId },
        }
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      );
  }

  getPublicLocations(
    request: PublicLocationRequest
  ): Observable<PublicLocation[]> {
    return this.http
      .post<BaseResponse<PublicLocationWebEntity[]>>(
        `${this.environment.url}${LocationsApiUrls.GetPublicLocations}`,
        request
      )
      .pipe(
        mergeMap(
          (response: BaseResponse<PublicLocationWebEntity[]>) => response.result
        ),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.publicLocationMapper.mapFrom))
      .pipe(
        reduce(
          (items: PublicLocation[], item: PublicLocation) => [...items, item],
          []
        )
      );
  }

  getPrivateLocations(
    request: PrivateLocationRequest
  ): Observable<GetPrivateLocationsResponse> {
    return this.http
      .post<BaseResponse<GetPrivateLocationWebEntity>>(
        `${this.environment.url}${LocationsApiUrls.GetPrivateLocations}`,
        request
      )
      .pipe(
        map(
          (response: BaseResponse<GetPrivateLocationWebEntity>) =>
            response.result
        ),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.getPrivateLocationMapper.mapFrom));
  }

  getDawaAutoCompleteLocations(request: string): Observable<Dawa[]> {
    return this.http
      .get<DawaWebEntity[]>(
        `${this.environment.dawaUrl}${LocationsApiUrls.GetDawaAutoCompleteLocations}`,
        {
          params: { q: request },
        }
      )
      .pipe(
        mergeMap((response: DawaWebEntity[]) => response),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.dawaMapper.mapFrom))
      .pipe(reduce((items: Dawa[], item: Dawa) => [...items, item], []));
  }
  
  getLocationsByAddressFromGLS(request: string): Observable<Gls[]> {
    return this.http
      .get<BaseResponse<GlsWebEntity[]>>(
        `${this.environment.url}${LocationsApiUrls.GetLocationsByAddressFromGLS}`,
        {
          params: { searchText: request },
        }
      )
      .pipe(
        map((response: BaseResponse<GlsWebEntity[]>) => response.result || []),
        mergeMap((webEntities: GlsWebEntity[]) => webEntities.length > 0 ? webEntities : []),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.glsWebRepositoryMapper.mapFrom))
      .pipe(reduce((items: Gls[], item: Gls) => [...items, item], []));
  }

  addPublicLocation(
    request: CreateEditPublicLocationRequest
  ): Observable<string> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${LocationsApiUrls.AddPublicLocation}`,
        request
      )
      .pipe(
        map((data: BaseResponse<string>) => {
          return data.result;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  getLocationType(): Observable<LocationType[]> {
    return this.http
      .get<BaseResponse<LocationTypeWebEntity[]>>(
        `${this.environment.url}${LocationsApiUrls.GetLocationTypes}`
      )
      .pipe(
        mergeMap(
          (response: BaseResponse<LocationTypeWebEntity[]>) => response.result
        ),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.locationTypeWebRepositoryMapper.mapFrom))
      .pipe(
        reduce(
          (items: LocationType[], item: LocationType) => [...items, item],
          []
        )
      );
  }

  importLocationsFromFile(request: FormData): Observable<boolean> {
    return this.http
      .post<BaseResponse<unknown>>(
        `${this.environment.url}${LocationsApiUrls.ImportLocationsFromFile}`,
        request,
        { headers: { skip: 'true' } }
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      );
  }

  getSampleLocationsImportFiles(): Observable<HttpResponse<Blob>> {
    return this.http
      .get(
        `${this.environment.url}${LocationsApiUrls.GetLocationsImportSampleFile}`,
        {
          responseType: 'blob',
          observe: 'response',
        }
      )
      .pipe(
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      );
  }

  getSegmentSuggestions(
    request: GetSegmentSuggestionsRequest
  ): Observable<SegmentSuggestions[]> {
    return this.http
      .post<BaseResponse<GetSegmentSuggestionsWebEntity[]>>(
        `${this.environment.url}${LocationsApiUrls.GetSegmentSuggestions}`,
        request
      )
      .pipe(
        mergeMap(
          (response: BaseResponse<GetSegmentSuggestionsWebEntity[]>) =>
            response.result
        ),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.segmentSuggestionMapper.mapFrom))
      .pipe(
        reduce(
          (items: SegmentSuggestions[], item: SegmentSuggestions) => [
            ...items,
            item,
          ],
          []
        )
      );
  }
  getPrivateLocationByMlsId(
    request: string
  ): Observable<PrivateLocationsForPP> {
    return this.http
      .get<BaseResponse<PrivateLocationsForPPWebEntity>>(
        `${this.environment.url}${LocationsApiUrls.GetPrivateLocationByMlsId}`,
        {
          params: { mlsId: request },
        }
      )
      .pipe(
        map(
          (response: BaseResponse<PrivateLocationsForPPWebEntity>) =>
            response.result!
        ),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.privateLocationsForPPMapper.mapFrom));
  }
  getStopsForPlan(
    request: GetStopsForPlanRequest
  ): Observable<PrivateLocationsForPP[]> {
    return this.http
      .post<BaseResponse<PrivateLocationsForPPWebEntity[]>>(
        `${this.environment.url}${LocationsApiUrls.GetPublicLocationsForPlan}`,
        request
      )
      .pipe(
        mergeMap(
          (response: BaseResponse<PrivateLocationsForPPWebEntity[]>) =>
            response.result
        ),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.privateLocationsForPPMapper.mapFrom))
      .pipe(
        reduce(
          (items: PrivateLocationsForPP[], item: PrivateLocationsForPP) => [
            ...items,
            item,
          ],
          []
        )
      );
  }

  getWayPointsForJob(request: string): Observable<number[][]> {
    return this.http
      .get<BaseResponse<number[][]>>(
        `${this.environment.planningPortalUrl}${LocationsApiUrls.GetJobWayPoints}`,
        {
          params: { id: request },
        }
      )
      .pipe(
        map((response) => response.result),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      );
  }

  /**
   * Call for country api to fetch the data and update the api
   * Call for Repository to network API.
   */

  getCountryData(): Observable<CountryEntity[]> {
    return this.http
      .get<BaseResponse<CountryWebEntity[]>>(
        `${this.environment.url}${LocationsApiUrls.GetCountries}`
      )
      .pipe(
        map((response) => response.result),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      );
  }

  getSortingOptions(): Observable<SortingOptions[]> {
    const options: SortingOptions[] = [
      { name: 'City', id: 'City' },
      { name: 'Title', id: 'Title' },
      { name: 'Postal Code', id: 'PostalCode' },
    ];
    return of(options);
  }
    exportLocations(): Observable<HttpResponse<Blob>> {  
    return this.http
      .get(`${this.environment.url}${LocationsApiUrls.ExportLocations}`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        catchError((err) => {
          throw err;
        })
      );
  }
}
